<template>
  <div class="home skybox-page">
    <div class="view-container" ref="threeDBox"></div>

    <transition name="sky-fade">
      <div v-if="sceneLoading" class="sky-preloader">
        <div class="preloader-card">
          <div class="preloader-ring">
            <span></span>
            <i></i>
          </div>
          <div class="preloader-copy">
            <strong>正在进入情景训练</strong>
            <em>{{ loadingStep }}</em>
          </div>
          <div class="preloader-progress">
            <span></span>
          </div>
        </div>
      </div>
    </transition>

    <transition name="sky-swipe">
      <div v-if="sceneTransition" class="scene-transition-layer">
        <div class="transition-scanner"></div>
        <div class="transition-text">{{ transitionText }}</div>
      </div>
    </transition>

    <button class="sky-back-btn" @click="handleBack">
      <i class="el-icon-arrow-left"></i>
      <span>返回课程</span>
    </button>

    <div class="sky-top-title">
      <span class="title-kicker">Scenario Training</span>
      <span class="title-main">课程：{{ currentTitle }}</span>
    </div>

    <div class="sky-brand-badge">
      <img src="@/assets/image/home/smart-cube-mark.png" alt="智训立方" />
      <div class="brand-text">
        <strong>智训立方</strong>
        <span>Smart Training Cube</span>
      </div>
    </div>

    <div class="sky-status-panel">
      <div class="status-head">
        <span class="live-dot"></span>
        <strong>情景状态</strong>
      </div>
      <div class="status-grid">
        <div>
          <em>观察模式</em>
          <span>360°</span>
        </div>
        <div>
          <em>交互热点</em>
          <span>{{ currentHotspotCount }}</span>
        </div>
        <div>
          <em>当前情景</em>
          <span>{{ currentSceneIndex + 1 }}/{{ caseContentList.length || 0 }}</span>
        </div>
      </div>
      <p>{{ sceneStatus }}</p>
    </div>

    <!-- 指南针组件 -->
    <div class="compass-container">
      <div class="compass" ref="compass">
        <div class="compass-rose">
          <div class="compass-north">北</div>
          <div class="compass-east">东</div>
          <div class="compass-south">南</div>
          <div class="compass-west">西</div>
          <div
            class="compass-needle"
            :style="{ transform: `rotate(${compassRotation}deg)` }"
          >
            <div class="needle-north"></div>
            <div class="needle-south"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="left-box-fix sky-side-panel">
      <div class="panel-header">
        <span class="panel-dot"></span>
        <div>
          <div class="panel-title">情景控制</div>
          <div class="panel-subtitle">Scene Control</div>
        </div>
      </div>

      <el-button type="primary" @click="handleCaseVideo" class="mb-10 custom-btn">
        <img class="img-box" src="@/assets/image/skybox/video.png" alt="" />
        案例视频
      </el-button>
      <el-button
        type="primary"
        :disabled="isFirstScene"
        @click="handlePrev"
        class="mb-10 custom-btn"
        :class="{ disabled: isFirstScene }"
      >
        <img class="img-box" src="@/assets/image/skybox/prev.png" alt="" />
        上一场景
      </el-button>
      <el-button
        type="primary"
        :disabled="isLastScene"
        @click="handleNext"
        class="mb-10 custom-btn"
        :class="{ disabled: isLastScene }"
      >
        <img class="img-box" src="@/assets/image/skybox/next.png" alt="" />
        下一场景
      </el-button>

      <div class="scene-info">
        <span>当前进度</span>
        <strong>{{ currentSceneIndex + 1 }} / {{ caseContentList.length || 0 }}</strong>
        <em>拖拽视角观察，点击热点进入情景任务</em>
      </div>
    </div>

    <div class="sky-hint-bar">
      <span><b></b>360° 情景观察</span>
      <i></i>
      <em>拖拽视角 · 点击热点 · 切换场景</em>
    </div>

    <!-- 进入后的欢迎弹窗 -->
    <el-dialog
      custom-class="dialogCustom"
      :title="WelcomePopup.Title"
      :visible.sync="centerDialogVisibleWelcome"
      width="50%"
    >
      <div class="welcome-title">{{ WelcomePopup.Title }}</div>
      <el-row :gutter="20">
        <el-col :xs="24" :lg="12">
          <div class="welcome-image-box">
            <el-image
              v-if="WelcomePopup.ImagePath"
              :src="prefix_url + imageBaseUrl + WelcomePopup.ImagePath"
            ></el-image>
          </div>
        </el-col>
        <el-col :xs="24" :lg="12">
          <span class="info-content">{{ WelcomePopup.Text }}</span>
        </el-col>
      </el-row>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="handleCloseWelcome">进入情景</el-button>
      </span>
    </el-dialog>

    <!-- 案例视频 -->
    <el-dialog
      custom-class="dialogCustomAi"
      :title="showInfo.Title"
      :visible.sync="centerDialogVisibleCase"
      width="50%"
    >
      <video :src="baseUrl + videoUrl" controls class="video-player"></video>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="centerDialogVisibleCase = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 展示ai助手弹窗：保留原逻辑，当前入口已隐藏 -->
    <el-dialog
      custom-class="dialogCustomAi"
      :title="showInfo.Title"
      :visible.sync="centerDialogVisibleAi"
      width="50%"
    >
      <iframe
        src="https://dify.zhi119.com/chat/xl7bzvmumq2turf6"
        style="width: 100%; height: 100%; min-height: 700px"
        frameborder="0"
        allow="microphone"
      >
      </iframe>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="centerDialogVisibleAi = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 展示信息弹窗 -->
    <el-dialog
      custom-class="dialogCustom"
      :title="showInfo.Title"
      :visible.sync="centerDialogVisible"
      width="50%"
      :show-close="false"
    >
      <el-row :gutter="20">
        <el-col :xs="24" :lg="12">
          <el-image
            v-if="showInfo.ImagePath"
            :src="prefix_url + imageBaseUrl + showInfo.ImagePath"
          ></el-image>
        </el-col>
        <el-col :xs="24" :lg="12">
          <span class="info-content">{{ showInfo.Text }}</span>
        </el-col>
      </el-row>
      <span slot="footer" class="dialog-footer">
        <div @click="centerDialogVisible = false" class="cancel-btn">关闭</div>
      </span>
    </el-dialog>

    <!-- 选择弹窗 -->
    <el-dialog
      custom-class="dialogCustom"
      :title="selectInfo.Title"
      :visible.sync="selectDialogVisible"
      width="50%"
    >
      <div>
        <el-row :gutter="20">
          <el-col :xs="24" :lg="12">
            <el-image
              v-if="selectInfo.ImagePath"
              :src="prefix_url + imageBaseUrl + selectInfo.ImagePath"
            ></el-image>
          </el-col>
          <el-col :xs="24" :lg="12">
            <div
              class="info-content"
              v-for="(item, index) in selectInfo.Answers"
              :key="index"
              @click="handleSelectIndex(index)"
              :class="{ active: currentSelectIndex === index }"
            >
              <div class="s-left">{{ handleText(index) }}</div>
              <div class="s-right">{{ item }}</div>
            </div>
          </el-col>
        </el-row>
      </div>

      <el-dialog
        custom-class="dialogCustom"
        width="40%"
        :title="selectInfo.Title"
        :visible.sync="selectInnerVisible"
        append-to-body
      >
        <div class="inner-content">
          <div class="text">
            {{ selectInnerInfo }}
          </div>
        </div>
        <div class="footer">
          <div class="select-inner-btn" @click="selectInnerVisible = false">
            关闭
          </div>
        </div>
      </el-dialog>
      <span slot="footer" class="footer">
        <div class="select-btn" @click="selectDialogVisible = false">关闭</div>
        <div class="select-btn" @click="handleSaveSelect">保存</div>
      </span>
    </el-dialog>

    <!-- 检伤弹窗 -->
    <el-dialog
      custom-class="dialogCustomInjury"
      :title="injuryInfo && injuryInfo.Victim && injuryInfo.Victim.ProfileName"
      :visible.sync="injuryDialogVisible"
      width="40%"
    >
      <div class="injury-content">
        <el-col :lg="2">
          <div
            class="i-left"
            :style="{
              background:
                innerRightList[currentInjuryIndex] &&
                innerRightList[currentInjuryIndex].color,
            }"
          ></div>
        </el-col>
        <div>
          <el-col :xs="24" :lg="10">
            <div class="i-center">
              <el-col :xs="24" :lg="12">
                <div class="c-left" v-if="injuryInfo && injuryInfo.Victim">
                  <div>
                    {{ injuryInfo.Victim.Gender }} {{ injuryInfo.Victim.Age }}
                  </div>
                  <div>
                    {{ injuryInfo.Victim.UserName }}
                  </div>
                </div>
              </el-col>

              <el-col :xs="24" :lg="12">
                <div class="c-right">
                  <el-image :src="prefix_url + viewerBaseUrl + injuryInfo.GUID + '.png'">
                  </el-image>
                </div>
              </el-col>
            </div>
          </el-col>
          <el-col :xs="24" :lg="12">
            <div class="i-right">
              <div>
                <div
                  class="i-right-item"
                  :style="{ background: item.color }"
                  v-for="(item, index) in innerRightList"
                  :key="index"
                  :class="{ active: currentInjuryIndex === index }"
                  @click="handleInjuryClick(index)"
                >
                  {{ item.name }}
                </div>
              </div>
              <div class="close-btn" @click="injuryDialogVisible = false">
                关闭
              </div>
            </div>
          </el-col>
        </div>
      </div>
    </el-dialog>
  </div>
</template>


<script>
import {
  getCaseContentList,
  getHouseDetail,
  userAnswer,
  getSkyDetail,
} from "@/api/index";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { prefix_url } from "@/assets/js/const.js";
import { getIndexToLetter } from "@/utils/utils.js";
import cv from "opencv.js";
import { mapState } from "vuex";
export default {
  name: "SkyBox",
  data() {
    return {
      scene: null,
      camera: null,
      controls: null,
      renderer: null,
      axisHelper: null,
      box: null,
      sphere: null,
      centerPosition: { x: 0, y: 0, z: 0 },
      contentUrl: "",
      timer: {},
      boxMaterialsMap: {},
      hotspotsObjectMap: {},
      viewpointsObjectMap: {},
      //allDirectionList: ["posx", "negx", "posy", "negy", "negz", "posz"], // 左右、上下、后前
      allDirectionList: ["negx", "posx", "posy", "negy", "posz", "negz"],
      picList: ["left", "right", "top", "bottom", "front", "back"],
      tipsSpriteList: [],
      tooltipPosition: {
        top: "-100%",
        left: "-100%",
      },
      titlePosition: {
        top: "-100%",
        left: "-100%",
      },
      tooltopContent: {},
      centerDialogVisible: false, // 展示信息弹窗是否显示
      selectDialogVisible: false, // 选择弹窗
      selectInnerVisible: false, // 选择内部弹窗
      currentSelectIndex: -1,
      selectInnerInfo: "", // 选择内部信息
      injuryDialogVisible: false, // 检伤弹窗
      currentInjuryIndex: -1,
      showInfo: {}, // 信息展示
      selectInfo: {}, // 选择信息
      injuryInfo: {}, // 检伤信息
      tipsList: [],
      playingVideo: null,
      innerRightList: [
        {
          name: "T1",
          color: "#eb1c24",
        },
        {
          name: "T2",
          color: "#fdea01",
        },
        {
          name: "T3",
          color: "#019541",
        },
        {
          name: "T3",
          color: "#1e1d1b",
        },
      ], // 检伤右侧内容
      imageBaseUrl: "", // 信息展示图片地址
      viewerBaseUrl: "", // 视图图片地址
      prefix_url,
      centerDialogVisibleAi: false, // AI助手弹窗是否显示
      caseContentList: [],
      currentSceneIndex: 0, // 添加这一行 - 当前场景索引
      caseCode: "",
      videoUrl: "",
      centerDialogVisibleCase: false,
      centerDialogVisibleWelcome: false,
      WelcomePopup: {},
      contentId: "",
      currentTitle: "",
      skyDetail: {},
      compassRotation: 0, // 指南针旋转角度
      answerList: [],
      sceneLoading: true,
      loadingStep: "加载全景资源 · 初始化观察视角 · 准备交互热点",
      sceneTransition: false,
      transitionText: "场景切换中",
      sceneStatus: "资源准备中",
      currentHotspotCount: 0,
      hotspotGlowTexture: null,
    };
  },
  computed: {
    ...mapState(["baseUrl", "name", "roles"]),
    // 是否是第一个场景
    isFirstScene() {
      return this.currentSceneIndex === 0;
    },
    // 是否是最后一个场景
    isLastScene() {
      return this.currentSceneIndex === this.caseContentList.length - 1;
    },
  },
  methods: {
    // 提交问题
    handleAnswer() {
      // 检查用户是否已登录
      if (this.roles.includes("student") || this.roles.includes("teacher")) {
        const params = {
          contentId: this.contentId,
          questionCode: this.selectInfo.GUID,
          answer: this.currentSelectIndex,
        };

        userAnswer(params)
          .then((res) => {
            this.selectDialogVisible = false;
            this.selectInnerVisible = true;
            this.selectInnerInfo = res.msg;
          })
          .catch((error) => {
            console.error("提交失败", error);
            this.$message.error("提交失败，请重试");
          });
      } else {
        this.$message.warning("请先登录");
      }
    },
    handleCloseWelcome() {
      // 数字人/讲解视频层已下线：仅关闭欢迎弹窗，不再自动播放右下角外接角色视频。
      this.centerDialogVisibleWelcome = false;
    },
    triggerSceneTransition(text = "场景切换中") {
      this.transitionText = text;
      this.sceneTransition = true;
    },
    finishSceneEffects(delay = 520) {
      window.setTimeout(() => {
        this.sceneLoading = false;
        this.sceneTransition = false;
      }, delay);
    },
    handleCaseVideo() {
      this.centerDialogVisibleCase = true;
    },
    handleAiShowDialog() {
      this.centerDialogVisibleAi = true;
    },
    getCaseContentList() {
      return new Promise((resolve, reject) => {
        const params = {
          caseCode: this.caseCode,
        };
        getCaseContentList(params)
          .then((result) => {
            if (result && result.code == 200) {
              this.caseContentList = result.rows;
              resolve(true);
            }
          })
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      });
    },
    // 上一场景
    handlePrev() {
      if (this.isFirstScene) return;
      // 如果视频在播放，则暂停
      if (this.playingVideo) {
        this.playingVideo.pause();
      }

      this.triggerSceneTransition("正在切换上一场景");
      this.currentSceneIndex--;
      const prevScene = this.caseContentList[this.currentSceneIndex];
      this.getDetail(prevScene.id);
    },
    // 下一场景
    handleNext() {
      if (this.isLastScene) return;
      // 如果视频在播放，则暂停
      if (this.playingVideo) {
        this.playingVideo.pause();
      }

      this.triggerSceneTransition("正在切换下一场景");
      this.currentSceneIndex++;
      const nextScene = this.caseContentList[this.currentSceneIndex];
      this.getDetail(nextScene.id);
    },
    // 获取详情数据
    getDetail(id) {
      if (!this.renderer) {
        this.sceneLoading = true;
        this.loadingStep = "加载全景资源 · 初始化观察视角 · 准备交互热点";
      }
      const params = {
        id,
      };
      this.contentId = id;
      getHouseDetail(params)
        .then((result) => {
          if (result && result.code == 200) {
            this.answerList = result.answerList;
            this.imageBaseUrl = result.imageBaseUrl;
            const url = result.viewerBaseUrl; // 内容映射地址
            this.viewerBaseUrl = url;
            const pointList = result.info.Viewpoints; // 观察点
            const startViewpointGUID = result.info.StartViewpoint; // 开始观察点GUID
            const hotspotsList = result.info.Hotspots; // 热点
            this.contentUrl = prefix_url + url; // 内容实际地址

            this.WelcomePopup = result.info.WelcomePopup || {};
            if (Object.keys(this.WelcomePopup).length > 0) {
              this.centerDialogVisibleWelcome = true;
            }

            //-----------------------处理数据-----------------------

            // 清理之前的数据
            this.boxMaterialsMap = {};
            this.hotspotsObjectMap = {};
            this.viewpointsObjectMap = {};

            //处理Viewpoints
            pointList.forEach((item) => {
              let GUID = item.GUID;
              //材质
              if (item.DisplayType === "Image") {
                this.boxMaterialsMap[GUID] = this.allDirectionList.map(
                  (item) => {
                    let texture = new THREE.TextureLoader().load(
                      `${this.contentUrl}${GUID}/${item}.jpg`
                    );
                    return new THREE.MeshBasicMaterial({ map: texture });
                  }
                );
              } else if (item.DisplayType === "Video") {
                //动态添加<video>标签
                //<video id="video" loop muted crossOrigin="anonymous" playsinline style="display:none">
                // 	<source src="textures/pano.webm">
                // 	<source src="textures/pano.mp4">
                // </video>
                let video = document.createElement("video");
                video.id = GUID;
                video.loop = true;
                video.muted = true;
                video.crossOrigin = "anonymous";
                video.playsinline = true;
                video.style.display = "none";
                let source = document.createElement("source");
                source.src = `${this.contentUrl}${GUID}/video.mp4`;
                //source.src="/vr1.mp4";
                video.appendChild(source);
                document.body.appendChild(video);

                let texture = new THREE.VideoTexture(
                  document.getElementById(GUID)
                );
                texture.encoding = THREE.sRGBEncoding;
                this.boxMaterialsMap[GUID] = new THREE.MeshBasicMaterial({
                  map: texture,
                });
              }

              //观察点对象
              this.viewpointsObjectMap[GUID] = item;
            });
            //处理Hotspots
            hotspotsList.forEach((item) => {
              //热点对象
              this.hotspotsObjectMap[item.GUID] = item;
            });

            //-----------------------渲染-----------------------

            let element = this.$refs.threeDBox;

            // 清理之前的渲染器
            if (this.renderer) {
              this.renderer.dispose();
              element.removeChild(this.renderer.domElement);
            }

            // 清理之前的控制器
            if (this.controls) {
              this.controls.dispose();
            }

            // 清理场景中的对象
            if (this.scene) {
              while (this.scene.children.length > 0) {
                this.scene.remove(this.scene.children[0]);
              }
            }

            this.initScene();
            this.initCamera(element);
            this.initControls(element);
            this.initGeometry();
            this.initRenderer(element);
            this.render();

            // 移除之前的事件监听器（如果有的话）
            window.removeEventListener("resize", this.onResize);
            window.removeEventListener("click", this.onMouseClick);

            window.addEventListener("resize", this.onResize, false);
            window.addEventListener("click", this.onMouseClick, false);
            // 切换初始场景
            this.changeContentAndtips(startViewpointGUID);
            this.finishSceneEffects();
          }
        })
        .catch((err) => {
          console.log(err);
          this.sceneLoading = false;
          this.sceneTransition = false;
        });
    },
    initScene() {
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x101010);

      // 辅助线 红色代表 X 轴. 绿色代表 Y 轴. 蓝色代表 Z 轴.
      // let axes = new THREE.AxesHelper(20);
      // this.scene.add(axes);
    },
    initCamera(element) {
      this.camera = new THREE.PerspectiveCamera(
        50,
        element.clientWidth / element.clientHeight,
        1,
        2000
      );
      this.camera.position.set(0, 0, 1);
    },
    initControls(element) {
      this.controls = new OrbitControls(this.camera, element);
      this.controls.dampingFactor = 0.05;
      this.controls.minDistance = 1;
      this.controls.maxDistance = 100;
      this.controls.enableZoom = false; // 启用或禁用摄像机的缩放。
      this.controls.enablePan = false; // 启用或禁用摄像机平移
      this.controls.screenSpacePanning = false;
      this.controls.maxPolarAngle = Math.PI;
      this.controls.target.set(0, 0, 0.000000000001);
    },
    initRenderer(element) {
      this.renderer = new THREE.WebGLRenderer();
      this.renderer.setSize(element.offsetWidth, element.offsetHeight);
      element.appendChild(this.renderer.domElement);
    },
    initGeometry() {
      this.initBox();
      this.initSphere();
    },
    initBox() {
      let boxGeometry = new THREE.BoxGeometry(500, 500, 500);
      this.box = new THREE.Mesh(boxGeometry);
      this.box.position.set(0, 0, 0);
      this.box.geometry.scale(1, 1, -1);
      this.scene.add(this.box);
      this.box.visible = false;
    },
    initSphere() {
      let sphereGeometry = new THREE.SphereGeometry(500, 64, 32);
      this.sphere = new THREE.Mesh(sphereGeometry);
      this.sphere.position.set(0, 0, 0);
      //this.sphere.geometry.scale(-4, 1, 4);
      this.sphere.geometry.scale(-1, 0.56, 1);

      this.sphere.rotation.y = -Math.PI / 2; // 90°
      this.scene.add(this.sphere);
      this.sphere.visible = false;
    },
    // 渲染窗口
    render() {
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      this.updateCompass(); // 更新指南针方向
      this.updateHotspotPulse(); // 更新热点呼吸光效
      this.timer = requestAnimationFrame(this.render);
    },
    // 更新指南针方向
    updateCompass() {
      if (this.camera && this.controls) {
        // 获取相机的欧拉角
        const euler = new THREE.Euler().setFromQuaternion(
          this.camera.quaternion,
          "YXZ"
        );
        // 将弧度转换为角度，并取负值（因为指南针方向与相机方向相反）
        this.compassRotation = -euler.y * (180 / Math.PI);
      }
    },
    // 重置窗口
    onResize() {
      let element = this.$refs.threeDBox;
      this.camera.aspect = element.clientWidth / element.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(element.clientWidth, element.clientHeight);
    },
    // 改变场景  进入其他地方
    changeContentAndtips(GUID) {
      this.scene.children = this.scene.children.filter(
        (item) => String(item.type) !== "Sprite"
      );

      if (this.playingVideo) {
        this.playingVideo.pause();
      }

      let viewpointObject = this.viewpointsObjectMap[GUID];
      //切换材质
      if (viewpointObject.DisplayType === "Video") {
        this.box.visible = false;
        this.sphere.visible = true;
        this.sphere.material = this.boxMaterialsMap[GUID];
        let video = document.getElementById(GUID);
        video.play();
        this.playingVideo = video;
      } else if (viewpointObject.DisplayType === "Image") {
        this.sphere.visible = false;
        this.box.visible = true;
        this.box.material = this.boxMaterialsMap[GUID];
      }

      // 移动动画
      // gsap.to(boxMaterials, { transparent: true, opacity: 1, duration: 2 });

      // 更新相机位置
      // this.camera.updateProjectionMatrix();

      // 处理标记点
      this.tipsSpriteList.forEach((item) => {
        this.scene.remove(item);
      });

      this.addCenterPosition(viewpointObject);
      this.addTipsSpriteByObj(viewpointObject);
      this.currentHotspotCount = (viewpointObject.Hotspots || []).length;
      this.sceneStatus = viewpointObject.DisplayType === "Video" ? "视频情景运行中" : "全景情景运行中";
      if (this.sceneTransition) {
        this.finishSceneEffects(460);
      }
    },
    // 添加标记点位置信息 设置中心坐标
    addCenterPosition(obj) {
      this.centerPosition = {
        x: obj.Position.X,
        y: obj.Position.Y,
        z: obj.Position.Z,
      };
    },
    addTipsSpriteByObj(obj) {
      let hotspots = obj.Hotspots;
      //添加标记点
      this.tipsSpriteList = [];
      hotspots.forEach((item) => {
        this.addTipsSprite(item);
      });
    },
    offsetPosition(vec3) {
      //计算距离
      const distance = Math.sqrt(vec3.x * vec3.x + vec3.z * vec3.z);
      if (distance === 0) {
        return new THREE.Vector3(0, vec3.y, 0);
      }
      //计算缩放因子
      const scaleFactor = this.calculateScale(distance);
      // console.log("distance", distance, "new", distance * scaleFactor, "scale", scaleFactor);
      return new THREE.Vector3(
        vec3.x * scaleFactor,
        vec3.y,
        vec3.z * scaleFactor
      );
    },
    calculateScale(distance) {
      if (distance <= 0) return 0;
      const a = 0.85; // 控制初始斜率
      const k = 37; // 控制整体幅度
      // 计算新的距离
      const newDistance = k * Math.log10(a * distance + 1);
      //返回缩放因子
      return newDistance / distance;
    },
    createHotspotGlowTexture() {
      if (this.hotspotGlowTexture) return this.hotspotGlowTexture;
      const canvas = document.createElement("canvas");
      canvas.width = 160;
      canvas.height = 160;
      const ctx = canvas.getContext("2d");
      const cx = 80;
      const cy = 80;
      const gradient = ctx.createRadialGradient(cx, cy, 8, cx, cy, 74);
      gradient.addColorStop(0, "rgba(117, 230, 255, 0.95)");
      gradient.addColorStop(0.18, "rgba(58, 164, 255, 0.38)");
      gradient.addColorStop(0.48, "rgba(39, 139, 255, 0.18)");
      gradient.addColorStop(1, "rgba(39, 139, 255, 0)");
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(cx, cy, 74, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = "rgba(126, 233, 255, 0.78)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, 42, 0, Math.PI * 2);
      ctx.stroke();

      ctx.strokeStyle = "rgba(40, 145, 255, 0.45)";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(cx, cy, 62, 0.2, Math.PI * 1.65);
      ctx.stroke();

      ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      ctx.fill();

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      this.hotspotGlowTexture = texture;
      return texture;
    },
    updateHotspotPulse() {
      if (!this.tipsSpriteList || !this.tipsSpriteList.length) return;
      const t = performance.now() * 0.001;
      this.tipsSpriteList.forEach((item, index) => {
        if (!item || !item.userData) return;
        if (item.userData.isHotspotGlow) {
          const base = item.userData.baseScale || 8;
          const pulse = 1 + Math.sin(t * 2.2 + index * 0.35) * 0.14;
          item.scale.set(base * pulse, base * pulse, base * pulse);
          if (item.material) {
            item.material.opacity = 0.36 + Math.sin(t * 2.2 + index * 0.35) * 0.12;
          }
        }
        if (item.userData.isHotspotIcon) {
          const base = item.userData.baseScale || 4.2;
          const pulse = 1 + Math.sin(t * 1.8 + index * 0.18) * 0.035;
          item.scale.set(base * pulse, base * pulse, base * pulse);
        }
      });
    },
    // 添加标记点
    addTipsSprite(GUID) {
      let hotspot = this.hotspotsObjectMap[GUID];
      let type = hotspot.$type
        .replace("XVR.Expo.Data.Hotspots.", "")
        .replace(", XVR.Expo.Data", "");
      let tipTexture = new THREE.TextureLoader().load(
        require(`@/assets/image//icon/${type}.png`)
      );
      const hotspotContent = { GUID: GUID, type: type };

      const glowMaterial = new THREE.SpriteMaterial({
        map: this.createHotspotGlowTexture(),
        transparent: true,
        opacity: 0.42,
        depthTest: false,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      let glowSprite = new THREE.Sprite(glowMaterial);
      glowSprite.scale.set(8.2, 8.2, 8.2);
      glowSprite.content = hotspotContent;
      glowSprite.userData = { isHotspotGlow: true, baseScale: 8.2 };

      let material = new THREE.SpriteMaterial({
        map: tipTexture,
        transparent: true,
        opacity: 0.96,
        depthTest: false,
        depthWrite: false,
      });
      let sprite = new THREE.Sprite(material);
      sprite.scale.set(4.2, 4.2, 4.2);
      sprite.content = hotspotContent;
      sprite.userData = { isHotspotIcon: true, baseScale: 4.2 };

      const rawPosition = new THREE.Vector3(
        (hotspot.Position.X - this.centerPosition.x) * 10,
        hotspot.Position.Y - this.centerPosition.y,
        -(hotspot.Position.Z - this.centerPosition.z) * 10
      );
      let newPosition = this.offsetPosition(rawPosition);
      glowSprite.position.set(newPosition.x, newPosition.y, newPosition.z);
      sprite.position.set(newPosition.x, newPosition.y, newPosition.z);

      this.tipsSpriteList.push(glowSprite, sprite);
      this.scene.add(glowSprite);
      this.scene.add(sprite);
    },
    // 鼠标点击事件
    onMouseClick(e) {
      e.preventDefault();
      let element = this.$refs.threeDBox;
      let raycaster = new THREE.Raycaster();
      let mouse = new THREE.Vector2();
      mouse.x = (e.clientX / element.clientWidth) * 2 - 1;
      mouse.y = -(e.clientY / element.clientHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, this.camera);
      let intersects = raycaster.intersectObjects(this.tipsSpriteList, true);

      // 这里判断是否点击到标记点
      if (intersects.length > 0) {
        let content = intersects[0].object.content;
        // HotspotTeleport   场景点
        // HotspotInfo			信息点
        // HotspotChoice		选择问题点
        // HotspotTriage		伤检分级点
        switch (content.type) {
          case "HotspotTeleport":
            this.triggerSceneTransition("正在进入热点场景");
            this.changeContentAndtips(content.GUID);
            break;
          case "HotspotInfo":
            //TODO 弹出信息窗
            this.centerDialogVisible = true;
            this.selectDialogVisible = false;
            this.injuryDialogVisible = false;
            this.showInfo = this.hotspotsObjectMap[content.GUID];
            console.log("信息展示", this.showInfo);
            break;
          case "HotspotChoice":
            //TODO 弹出选择窗
            this.selectDialogVisible = true;
            this.centerDialogVisible = false;
            this.injuryDialogVisible = false;
            this.selectInfo = this.hotspotsObjectMap[content.GUID];
            console.log("选择展示", this.selectInfo);
            console.log("当前案例详情", this.skyDetail);

            // 判断详情内有没有选择过
            this.answerList.forEach((item) => {
              if (item.questionCode == this.selectInfo.GUID) {
                this.currentSelectIndex = Number(item.answer);
              }
            });

            break;
          case "HotspotTriage":
            //TODO 弹出伤检窗
            this.injuryDialogVisible = true;
            this.centerDialogVisible = false;
            this.selectDialogVisible = false;
            this.injuryInfo = this.hotspotsObjectMap[content.GUID];
            console.log("检伤展示", this.injuryInfo);
            this.$nextTick(() => {
              this.initCanvas();
            });
            break;
        }
      }
    },
    handleText(index) {
      return getIndexToLetter(index);
    },
    handleSelectIndex(index) {
      this.currentSelectIndex = index;
    },
    handleSaveSelect() {
      this.handleAnswer();
    },
    handleInjuryClick(index) {
      this.currentInjuryIndex = index;
    },
    initCanvas() {
      let canvas = this.$refs.triageCanvas;
      if (!canvas) return;
      let ctx = canvas.getContext("2d");
      //浅灰色背景
      ctx.fillStyle = "#f6f6f6";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      let img = new Image();
      img.src = require("@/assets/image/icon/body_red_green.png");
      img.onload = () => {
        let src = cv.imread(img); // 你的 PNG 图像
        let dst = src.clone();
        let gray = new cv.Mat();
        let contours = new cv.MatVector();
        let hierarchy = new cv.Mat();

        // 转换图像到灰度
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY, 0);

        // 找到图像中的轮廓
        cv.findContours(
          gray,
          contours,
          hierarchy,
          cv.RETR_CCOMP,
          cv.CHAIN_APPROX_SIMPLE
        );
        // 检查是否找到了轮廓
        if (contours.size() == 0) return;
        cv.drawContours(dst, contours, 22, [0, 0, 0, 255], -1);

        //头部10 22 胸部9 背面 21 会阴5 左臂8 20 右臂6 19 左腿1 13  右腿2 12

        //去除辅助线
        // 定义绿色的范围
        let lowerb = new cv.Mat(
          src.rows,
          src.cols,
          src.type(),
          [30, 150, 0, 0]
        );
        let upperb = new cv.Mat(
          src.rows,
          src.cols,
          src.type(),
          [50, 200, 90, 255]
        );
        // 找到所有绿色的像素
        let mask = new cv.Mat();
        cv.inRange(src, lowerb, upperb, mask);

        // 将所有绿色的像素设置为透明
        for (let i = 0; i < src.rows; i++) {
          for (let j = 0; j < src.cols; j++) {
            if (mask.ucharPtr(i, j)[0] !== 0) {
              dst.ucharPtr(i, j)[3] = 0; // 绿色设为透明
            } else {
              dst.ucharPtr(i, j)[0] = 0; // 其他设为黑色
            }
          }
        }

        let dsize = new cv.Size(canvas.width, canvas.height);
        cv.resize(dst, dst, dsize, 0, 0, cv.INTER_AREA);

        cv.imshow(canvas, dst); // 显示结果

        // 释放内存
        src.delete();
        dst.delete();
        gray.delete();
        contours.delete();
        hierarchy.delete();
        lowerb.delete();
        upperb.delete();
        mask.delete();
        pattern.delete();
        patternResized.delete();
        mask1.delete();
        dstVec.delete();
        patternVec.delete();
      };
      // ctx.strokeStyle = 'red';
      // ctx.lineWidth = 5;
      // ctx.moveTo(55, 40);
      // ctx.lineTo(80, 40);
      // ctx.stroke();
      //头部 胸部 背面 会阴 左臂 右臂 左腿 右腿
    },
    randerCanvas() {
      //添加图片
      // let canvas = this.$refs.triageCanvas;
      // let ctx = canvas.getContext("2d");
      // let img = new Image();
      // img.src = require("@/assets/image/icon/1.png");
      // img.onload = function () {
      //   ctx.drawImage(img, 0, 0, 300, 300);
      // };
    },
    handleBack() {
      this.$router.go(-1);
    },
    getSkyDetail() {
      const params = {
        id: this.caseCode,
      };
      getSkyDetail(params)
        .then((response) => {
          if (response && response.code == 200) {
            this.skyDetail = response.data;
            this.videoUrl = this.skyDetail.videoUrl;
            this.currentTitle = this.skyDetail.title;
            document.title="智训立方："+this.skyDetail.title;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
  async mounted() {
    this.caseCode = this.$route.query.id;

    try {
      this.getSkyDetail();
      await this.getCaseContentList();
      if (this.caseContentList.length > 0) {
        this.getDetail(this.caseContentList[this.currentSceneIndex].id);
      } else {
        this.sceneLoading = false;
      }
    } catch (error) {
      console.log(error);
    }
  },
  destroyed() {
    cancelAnimationFrame(this.timer);
    // 清理控制器
    if (this.controls) {
      this.controls.dispose();
    }
    // 清理渲染器
    if (this.renderer) {
      this.renderer.dispose();
    }
    // 移除所有事件监听器
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("click", this.onMouseClick);
  },
};
</script>

<style lang="scss" scoped>
.home.skybox-page {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #061226;
  font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;

  .view-container {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
}



.sky-preloader {
  position: absolute;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 50% 40%, rgba(33, 154, 255, 0.2), transparent 28%),
    rgba(2, 9, 24, 0.72);
  backdrop-filter: blur(8px);
}

.preloader-card {
  width: 360px;
  padding: 28px;
  border: 1px solid rgba(120, 210, 255, 0.28);
  border-radius: 28px;
  color: #fff;
  text-align: center;
  background: linear-gradient(155deg, rgba(4, 18, 48, 0.88), rgba(12, 62, 126, 0.5));
  box-shadow: 0 32px 90px rgba(0, 12, 48, 0.58), inset 0 1px 0 rgba(255, 255, 255, 0.16);
}

.preloader-ring {
  position: relative;
  width: 92px;
  height: 92px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, rgba(43, 145, 255, 0.08), #21d6ff, #1d7cff, rgba(43, 145, 255, 0.08));
  animation: skySlowRotate 1.4s linear infinite;
}

.preloader-ring::after {
  content: "";
  position: absolute;
  inset: 9px;
  border-radius: 50%;
  background: #061733;
}

.preloader-ring span {
  position: absolute;
  inset: 24px;
  z-index: 1;
  border-radius: 50%;
  border: 1px solid rgba(116, 216, 255, 0.28);
  box-shadow: inset 0 0 18px rgba(76, 204, 255, 0.22);
}

.preloader-ring i {
  position: absolute;
  left: 50%;
  top: 6px;
  z-index: 2;
  width: 9px;
  height: 9px;
  margin-left: -4px;
  border-radius: 50%;
  background: #7ee9ff;
  box-shadow: 0 0 16px rgba(126, 233, 255, 0.9);
}

.preloader-copy strong {
  display: block;
  font-size: 22px;
  font-weight: 950;
  letter-spacing: 0.04em;
}

.preloader-copy em {
  display: block;
  margin-top: 8px;
  color: rgba(225, 244, 255, 0.68);
  font-size: 13px;
  font-style: normal;
  font-weight: 750;
}

.preloader-progress {
  position: relative;
  height: 4px;
  margin-top: 22px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.12);
}

.preloader-progress span {
  position: absolute;
  inset: 0 auto 0 0;
  width: 42%;
  border-radius: inherit;
  background: linear-gradient(90deg, #1d7cff, #20d8ff);
  animation: skyProgressMove 1.25s ease-in-out infinite;
}

.scene-transition-layer {
  position: absolute;
  inset: 0;
  z-index: 72;
  pointer-events: none;
  background: rgba(3, 12, 32, 0.22);
  overflow: hidden;
}

.transition-scanner {
  position: absolute;
  top: -18%;
  left: -18%;
  width: 34%;
  height: 136%;
  background: linear-gradient(90deg, transparent, rgba(71, 206, 255, 0.26), rgba(255, 255, 255, 0.16), transparent);
  transform: skewX(-18deg);
  animation: transitionSweep 0.72s ease-in-out forwards;
}

.transition-text {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding: 10px 20px;
  border: 1px solid rgba(139, 219, 255, 0.32);
  border-radius: 999px;
  color: #eaf8ff;
  background: rgba(4, 18, 48, 0.72);
  backdrop-filter: blur(12px);
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 0.08em;
  box-shadow: 0 18px 48px rgba(0, 22, 72, 0.32);
}

.sky-status-panel {
  position: absolute;
  top: 92px;
  right: 28px;
  z-index: 20;
  width: 212px;
  padding: 14px;
  border: 1px solid rgba(126, 205, 255, 0.24);
  border-radius: 20px;
  color: #fff;
  background: linear-gradient(155deg, rgba(4, 18, 46, 0.7), rgba(12, 61, 118, 0.36));
  backdrop-filter: blur(14px);
  box-shadow: 0 18px 45px rgba(0, 18, 58, 0.26), inset 0 1px 0 rgba(255,255,255,.11);
}

.status-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: rgba(236, 248, 255, 0.92);
  font-size: 13px;
  font-weight: 950;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2ce7a0;
  box-shadow: 0 0 14px rgba(44, 231, 160, 0.86);
  animation: livePulse 1.6s ease-in-out infinite;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.status-grid div {
  padding: 9px 6px;
  border: 1px solid rgba(126, 205, 255, 0.14);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.07);
  text-align: center;
}

.status-grid em {
  display: block;
  color: rgba(219, 238, 255, 0.58);
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
}

.status-grid span {
  display: block;
  margin-top: 4px;
  color: #7ee9ff;
  font-size: 14px;
  line-height: 1;
  font-weight: 950;
}

.sky-status-panel p {
  margin: 10px 2px 0;
  color: rgba(227, 243, 255, 0.66);
  font-size: 11px;
  line-height: 1.5;
  font-weight: 700;
}

.sky-fade-enter-active,
.sky-fade-leave-active { transition: opacity .28s ease; }
.sky-fade-enter,
.sky-fade-leave-to { opacity: 0; }

.sky-swipe-enter-active,
.sky-swipe-leave-active { transition: opacity .25s ease; }
.sky-swipe-enter,
.sky-swipe-leave-to { opacity: 0; }

@keyframes skySlowRotate { to { transform: rotate(360deg); } }
@keyframes skyProgressMove { 0% { left: -42%; } 55% { left: 22%; } 100% { left: 104%; } }
@keyframes transitionSweep { 0% { left: -34%; } 100% { left: 116%; } }
@keyframes livePulse { 0%,100% { transform: scale(1); opacity: .78; } 50% { transform: scale(1.45); opacity: 1; } }

.sky-back-btn {
  position: absolute;
  top: 24px;
  left: 28px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 16px;
  border: 1px solid rgba(106, 184, 255, 0.42);
  border-radius: 999px;
  color: #dff3ff;
  background: linear-gradient(135deg, rgba(10, 36, 82, 0.78), rgba(15, 105, 188, 0.58));
  box-shadow: 0 12px 30px rgba(0, 41, 96, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(14px);
  cursor: pointer;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 36px rgba(0, 83, 160, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.26);
  }
}

.sky-top-title {
  position: absolute;
  top: 22px;
  left: 50%;
  z-index: 20;
  transform: translateX(-50%);
  min-width: 360px;
  padding: 10px 24px;
  border: 1px solid rgba(106, 184, 255, 0.28);
  border-radius: 999px;
  color: #fff;
  text-align: center;
  background: linear-gradient(135deg, rgba(3, 15, 40, 0.7), rgba(16, 76, 142, 0.42));
  backdrop-filter: blur(14px);
  box-shadow: 0 14px 36px rgba(0, 24, 70, 0.28);

  .title-kicker {
    display: block;
    margin-bottom: 2px;
    color: #6ee7ff;
    font-size: 11px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .title-main {
    display: block;
    color: #f4fbff;
    font-size: 17px;
    line-height: 1.25;
    font-weight: 900;
    letter-spacing: 0.03em;
  }
}

.sky-brand-badge {
  position: absolute;
  top: 20px;
  right: 28px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px 8px 10px;
  border: 1px solid rgba(135, 202, 255, 0.28);
  border-radius: 18px;
  color: #fff;
  background: linear-gradient(135deg, rgba(5, 17, 44, 0.72), rgba(18, 87, 153, 0.36));
  backdrop-filter: blur(12px);
  box-shadow: 0 14px 36px rgba(0, 26, 72, 0.28);

  img {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    object-fit: contain;
    padding: 4px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 0 18px rgba(56, 203, 255, 0.3);
  }

  .brand-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  strong {
    font-size: 15px;
    line-height: 1.1;
    font-weight: 950;
    letter-spacing: 0.04em;
  }

  span {
    margin-top: 3px;
    color: rgba(213, 234, 255, 0.78);
    font-size: 10px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
}

.sky-side-panel {
  position: fixed;
  bottom: 32px;
  left: 28px;
  z-index: 20;
  width: 188px;
  height: auto;
  padding: 16px;
  border: 1px solid rgba(112, 189, 255, 0.32);
  border-radius: 22px;
  background: linear-gradient(155deg, rgba(3, 14, 40, 0.78), rgba(12, 64, 120, 0.46));
  backdrop-filter: blur(16px);
  box-shadow: 0 24px 54px rgba(0, 17, 54, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.14);

  .panel-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(142, 207, 255, 0.16);
  }

  .panel-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #38d5ff;
    box-shadow: 0 0 16px rgba(56, 213, 255, 0.86);
  }

  .panel-title {
    color: #fff;
    font-size: 14px;
    font-weight: 950;
    letter-spacing: 0.04em;
  }

  .panel-subtitle {
    margin-top: 2px;
    color: rgba(219, 238, 255, 0.56);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .custom-btn.el-button {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    width: 100%;
    height: 40px;
    margin: 0 0 9px 0;
    padding: 0 12px;
    border: 1px solid rgba(116, 190, 255, 0.26) !important;
    border-radius: 13px;
    color: #eaf7ff;
    font-size: 13px;
    font-weight: 850;
    background: rgba(255, 255, 255, 0.08) !important;
    transition: all 0.18s ease;

    &:hover {
      transform: translateX(2px);
      border-color: rgba(93, 204, 255, 0.58) !important;
      background: linear-gradient(135deg, rgba(32, 137, 255, 0.32), rgba(33, 207, 255, 0.16)) !important;
    }

    &.disabled,
    &.is-disabled {
      opacity: 0.46;
      cursor: not-allowed;
      transform: none;
    }
  }

  .img-box {
    width: 17px;
    height: 17px;
    object-fit: contain;
    opacity: 0.92;
  }

  .scene-info {
    margin-top: 10px;
    padding: 12px;
    border: 1px solid rgba(106, 184, 255, 0.18);
    border-radius: 16px;
    background: rgba(6, 22, 54, 0.48);

    span {
      display: block;
      color: rgba(217, 235, 255, 0.68);
      font-size: 11px;
      font-weight: 800;
    }

    strong {
      display: block;
      margin-top: 3px;
      color: #7ee9ff;
      font-size: 24px;
      line-height: 1.1;
      font-weight: 950;
      letter-spacing: 0.02em;
    }

    em {
      display: block;
      margin-top: 6px;
      color: rgba(232, 244, 255, 0.58);
      font-size: 11px;
      line-height: 1.45;
      font-style: normal;
    }
  }
}

.sky-hint-bar {
  position: fixed;
  left: 50%;
  bottom: 28px;
  z-index: 20;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border: 1px solid rgba(130, 202, 255, 0.24);
  border-radius: 999px;
  color: rgba(231, 244, 255, 0.82);
  background: rgba(3, 14, 40, 0.58);
  backdrop-filter: blur(12px);
  box-shadow: 0 18px 38px rgba(0, 12, 46, 0.26);

  span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #fff;
    font-size: 13px;
    font-weight: 900;

    b {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #2ce7a0;
      box-shadow: 0 0 14px rgba(44, 231, 160, 0.8);
    }
  }

  i {
    width: 1px;
    height: 14px;
    background: rgba(166, 219, 255, 0.28);
  }

  em {
    color: rgba(227, 243, 255, 0.66);
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
  }
}


.compass-container {
  position: absolute;
  top: 92px;
  left: 50%;
  z-index: 20;
  transform: translateX(-50%);
}

.compass {
  width: 66px;
  height: 66px;
  border: 1px solid rgba(126, 205, 255, 0.28);
  border-radius: 50%;
  background: rgba(4, 16, 42, 0.62);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(12px);
  box-shadow: 0 16px 36px rgba(0, 18, 58, 0.28), inset 0 0 0 1px rgba(255,255,255,.08);
}

.compass-rose {
  position: relative;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(100, 196, 255, 0.12), rgba(255, 255, 255, 0.04));
}

.compass-north,
.compass-east,
.compass-south,
.compass-west {
  position: absolute;
  font-size: 10px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.82);
}

.compass-north { top: 2px; left: 50%; transform: translateX(-50%); color: #ff6565; }
.compass-east { right: 2px; top: 50%; transform: translateY(-50%); }
.compass-south { bottom: 2px; left: 50%; transform: translateX(-50%); }
.compass-west { left: 2px; top: 50%; transform: translateY(-50%); }

.compass-needle {
  position: absolute;
  inset: 0;
  transition: transform 0.1s ease-out;
}

.needle-north,
.needle-south {
  position: absolute;
  left: 50%;
  width: 0;
  height: 0;
  transform: translateX(-50%);
}

.needle-north {
  top: 7px;
  border-left: 2px solid transparent;
  border-right: 2px solid transparent;
  border-bottom: 20px solid #ff4d4f;
}

.needle-south {
  bottom: 7px;
  border-left: 2px solid transparent;
  border-right: 2px solid transparent;
  border-top: 20px solid rgba(255, 255, 255, 0.38);
}

.video-player {
  width: 100%;
  max-height: 70vh;
  border-radius: 14px;
  background: #000;
}

.welcome-title {
  margin-bottom: 18px;
  color: #fff;
  font-size: 24px;
  font-weight: 950;
}

.welcome-image-box {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
}

::v-deep .dialogCustom,
::v-deep .dialogCustomAi {
  position: relative;
  border: 1px solid rgba(91, 174, 255, 0.28);
  border-radius: 22px;
  color: #fff;
  overflow: hidden;
  background:
    radial-gradient(circle at 86% 10%, rgba(46, 204, 255, 0.18), transparent 28%),
    linear-gradient(150deg, rgba(4, 13, 34, 0.96), rgba(8, 27, 70, 0.94)) !important;
  box-shadow: 0 30px 80px rgba(0, 9, 32, 0.48), inset 0 1px 0 rgba(255,255,255,.12);


  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, #22d8ff, #1d7cff, transparent);
    box-shadow: 0 0 18px rgba(52, 202, 255, 0.7);
  }

  &::after {
    content: "";
    position: absolute;
    right: -80px;
    top: -80px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(67, 214, 255, 0.18), transparent 62%);
    pointer-events: none;
  }

  .el-dialog__header {
    position: relative;
    padding: 24px 24px 10px;
    border-bottom: 1px solid rgba(120, 200, 255, 0.12);
  }

  .el-dialog__header::before {
    content: "Scenario Card";
    display: inline-flex;
    align-items: center;
    height: 22px;
    margin-bottom: 8px;
    padding: 0 10px;
    border: 1px solid rgba(126, 233, 255, 0.28);
    border-radius: 999px;
    color: #7ee9ff;
    background: rgba(45, 143, 255, 0.12);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: .08em;
  }

  .el-dialog__title {
    color: #eef8ff;
    font-size: 18px;
    font-weight: 950;
  }

  .el-dialog__body {
    position: relative;
    padding: 22px 24px;
  }

  .el-image {
    overflow: hidden;
    border: 1px solid rgba(126, 205, 255, 0.18);
    border-radius: 16px;
    background: rgba(255,255,255,.06);
    box-shadow: 0 16px 34px rgba(0, 15, 42, 0.24);
  }

  .el-dialog__footer {
    padding: 8px 24px 24px;
  }

  .el-button--primary {
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #1d7cff, #20c9ff);
    font-weight: 850;
  }

  .info-content {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    padding: 12px;
    border: 1px solid rgba(103, 184, 255, 0.18);
    border-radius: 14px;
    color: rgba(239, 248, 255, 0.92);
    background: rgba(255, 255, 255, 0.08);
    cursor: pointer;
    font-size: 15px;
    line-height: 1.7;
    transition: all .18s ease;

    &:hover {
      border-color: rgba(126, 233, 255, 0.38);
      background: rgba(45, 143, 255, 0.13);
      transform: translateY(-1px);
    }

    .s-left {
      width: 38px;
      height: 38px;
      line-height: 38px;
      margin-right: 10px;
      border-radius: 10px;
      color: #7ee9ff;
      background: rgba(45, 143, 255, 0.18);
      text-align: center;
      font-weight: 950;
    }

    .s-right {
      flex: 1;
    }
  }

  .active .s-left {
    color: #fff;
    background: linear-gradient(135deg, #1d7cff, #20c9ff);
  }

  .cancel-btn,
  .select-btn,
  .select-inner-btn {
    border-radius: 12px;
    color: #eaf7ff;
    background: rgba(255, 255, 255, 0.1);
    text-align: center;
    padding: 10px 0;
    cursor: pointer;
    transition: all 0.18s ease;

    &:hover {
      background: rgba(39, 153, 255, 0.28);
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    color: #fff;

    .select-btn {
      width: 50%;
    }

    .select-inner-btn {
      width: 100%;
    }
  }
}

::v-deep .dialogCustomInjury {
  border-radius: 20px;
  overflow: hidden;

  .injury-content {
    display: flex;

    .i-left {
      width: 10px;
      height: 300px;
      margin-right: 10px;
      border-radius: 8px;
    }

    .i-right {
      color: #fff;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      background: #f2f7ff;

      .i-right-item {
        cursor: pointer;
        padding: 8px 10px;
        margin-bottom: 10px;
        border: 2px solid transparent;
        border-radius: 10px;
        font-weight: 850;
      }

      .active {
        border-color: #1d7cff;
      }

      .close-btn {
        cursor: pointer;
        background: #fff;
        color: #10264c;
        padding: 10px 0;
        text-align: center;
        box-shadow: 0 6px 12px 2px #f5f7f7;
        font-weight: 850;
      }
    }
  }
}

.title-text,
.tooltip-box,
.compass-label {
  display: none;
}

@media screen and (max-width: 1024px) {
  .sky-status-panel {
    display: none;
  }

  .sky-top-title {
    top: 18px;
    min-width: 260px;
    max-width: 46vw;
    padding: 9px 16px;

    .title-main {
      font-size: 14px;
    }
  }

  .sky-brand-badge {
    right: 18px;
    padding: 7px 10px;

    img {
      width: 32px;
      height: 32px;
    }

    span {
      display: none;
    }
  }

  .sky-side-panel {
    left: 18px;
    bottom: 20px;
    width: 164px;
    padding: 12px;

    .custom-btn.el-button {
      height: 36px;
      font-size: 12px;
    }

    .scene-info strong {
      font-size: 20px;
    }
  }

  .sky-hint-bar {
    display: none;
  }

  .compass-container {
    top: 82px;
  }
}
</style>

